import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useFragrances } from '../../contexts/FragranceContext';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

const StatsDashboard: React.FC = () => {
  const { stats, fetchStats, isLoading } = useFragrances();
  const [copiedStats, setCopiedStats] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;
    fetchStats();
  }, [fetchStats, token]);

  const copyStatsToClipboard = () => {
    if (!stats) return;

    const statsText = `
Fragrance Collection Statistics
==============================

Collection Overview:
• Total Fragrances: ${stats.totals.fragrances}
• Total Decants/Samples: ${stats.totals.decants}
• Favorite Fragrances: ${stats.totals.favorites}
• Unique Houses: ${stats.totals.houses}

Average Ratings:
• Overall Rating: ${stats.averageRatings.overall}/10
• Longevity: ${stats.averageRatings.longevity}/5
• Sillage: ${stats.averageRatings.sillage}/4

Seasonal Preferences:
• Winter: ${stats.averageRatings.seasonal.winter}/5
• Spring: ${stats.averageRatings.seasonal.spring}/5
• Summer: ${stats.averageRatings.seasonal.summer}/5
• Fall: ${stats.averageRatings.seasonal.fall}/5

Top Houses:
${stats.topHouses.map(h => `• ${h.house}: ${h.count} fragrances`).join('\n')}

Concentration Breakdown:
${stats.concentrations.map(c => `• ${c.concentration}: ${c.count} fragrances`).join('\n')}

Most Used Notes:
${stats.topNotes.map(n => `• ${n.note}: ${n.count} times`).join('\n')}
    `.trim();

    // Check if navigator.clipboard is available (HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(statsText).then(() => {
        setCopiedStats(true);
        setTimeout(() => setCopiedStats(false), 2000);
      }).catch(err => {
        console.error('Failed to copy stats:', err);
      });
    } else {
      // Fallback for older browsers or HTTP
      try {
        const textArea = document.createElement('textarea');
        textArea.value = statsText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
        setCopiedStats(true);
        setTimeout(() => setCopiedStats(false), 2000);
      } catch (err) {
        console.error('Failed to copy stats (fallback):', err);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-600 dark:text-gray-400">No statistics available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Collection Statistics
        </h2>
        <Button
          onClick={copyStatsToClipboard}
          variant="outline"
          className={copiedStats ? 'bg-green-50 border-green-300 text-green-700' : ''}
        >
          {copiedStats ? (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Stats
            </>
          )}
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {stats.totals.fragrances}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total Fragrances
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
            {stats.totals.decants}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Decants & Samples
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">
            {stats.totals.favorites}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Favorites
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">
            {stats.totals.houses}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Unique Houses
          </div>
        </motion.div>
      </div>
      {/* Collection Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Collection Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Your Collection Profile
            </h4>
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>
                • You have <span className="text-black dark:text-white">{stats.totals.fragrances}</span> fragrances from <span className="text-black dark:text-white">{stats.totals.houses}</span> different houses
              </li>
              <li>
                • <span className="text-black dark:text-white">{((stats.totals.favorites / Math.max(stats.totals.fragrances, 1)) * 100).toFixed(1)}%</span> of your collection are favorites
              </li>
              <li>
                • <span className="text-black dark:text-white">{((stats.totals.decants / Math.max(stats.totals.fragrances + stats.totals.decants, 1)) * 100).toFixed(1)}%</span> are decants or samples
              </li>
              {stats.topHouses.length > 0 && (
                <li>
                  • Your most collected house is <span className="text-black dark:text-white">{stats.topHouses[0].house}</span> with <span className="text-black dark:text-white">{stats.topHouses[0].count}</span> fragrances
                </li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Rating Insights
            </h4>
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>
                • Average overall rating: <span className="text-black dark:text-white">{stats.averageRatings.overall}</span> / 10
              </li>
              <li>
                • Average longevity: <span className="text-black dark:text-white">{stats.averageRatings.longevity}</span> / 5
              </li>
              <li>
                • Average sillage: <span className="text-black dark:text-white">{stats.averageRatings.sillage}</span> / 4
              </li>
              <li>
                • Best season:{" "}
                <span className="text-black dark:text-white">
                  {Object.entries(stats.averageRatings.seasonal)
                    .sort(([, a], [, b]) => b - a)[0][0]
                    .charAt(0).toUpperCase() +
                    Object.entries(stats.averageRatings.seasonal)
                      .sort(([, a], [, b]) => b - a)[0][0]
                      .slice(1)}
                </span>{" "}
                -{" "}
                <span className="text-black dark:text-white">
                  {Math.max(...Object.values(stats.averageRatings.seasonal))}
                </span>{" "}
                <span className="text-gray-600 dark:text-gray-400">/ 5</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>
      {/* Average Ratings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Average Ratings
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.averageRatings.overall}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Overall Rating (out of 10)
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.averageRatings.longevity}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Longevity (out of 5)
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.averageRatings.sillage}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Sillage (out of 4)
            </div>
          </div>
        </div>
      </motion.div>
      {/* Concentration Types */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Concentration Types
        </h3>

        {stats.concentrations.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-6 text-center">
            {['EdC', 'EdT', 'EdP', 'Parfum', 'Elixir', 'Extrait'].map((key) => {
              const concMap: Record<string, string> = {
                EdC: 'Eau de Cologne',
                EdT: 'Eau de Toilette',
                EdP: 'Eau de Parfum',
                Parfum: 'Parfum',
                Elixir: 'Elixir',
                Extrait: 'Extrait de Parfum',
              };

              const concData = stats.concentrations.find(c => c.concentration === key);
              const count = concData ? concData.count : 0;
              const displayName = concMap[key];

              return (
                <div key={key} className="flex flex-col items-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    {count}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {displayName}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
            No concentration data available yet
          </p>
        )}
      </motion.div>
      {/* Top Houses + Seasonal Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Houses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top Houses
          </h3>
          {stats.topHouses.length > 0 ? (
            <div className="space-y-3">
              {stats.topHouses.slice(0, 8).map((house) => {
                // const percentage = (house.count / Math.max(...stats.topHouses.map(h => h.count))) * 100;
                const totalFragrances = stats.totals.fragrances;
                const percentage = (house.count / totalFragrances) * 100;
                return (
                  <div key={house.house} className="flex flex-col space-y-1">
                    <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 font-medium">
                      <span>{house.house}</span>
                      <span>{house.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              No house data available yet
            </p>
          )}
        </motion.div>
        {/* Seasonal Breakdown (horizontal bar chart style) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Seasonal Preferences
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.averageRatings.seasonal).map(([season, value]) => {
              const maxValue = Math.max(...Object.values(stats.averageRatings.seasonal));
              // const percentage = (value / maxValue) * 100;
              const percentage = (value / 5) * 100;
              const colorMap: Record<string, string> = {
                winter: 'bg-blue-400 dark:bg-blue-400',
                spring: 'bg-green-400 dark:bg-green-400',
                summer: 'bg-red-400 dark:bg-red-400',
                fall: 'bg-orange-400 dark:bg-orange-400',
              };
              return (
                <div key={season} className="flex flex-col space-y-1">
                  <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 font-medium capitalize">
                    <span>{season}</span>
                    <span>{value}/5</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                    <div
                      className={`${colorMap[season]} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
      {/* Most Common Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Most Common Notes
        </h3>
        {stats.topNotes.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stats.topNotes.slice(0, 12).map((note, index) => (
              <div
                key={note.note}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {note.note}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {note.count} times
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            No notes data available yet
          </p>
        )}
      </motion.div>

    </div>
  );
};

export default StatsDashboard;
